/**
 * "Frequently used" emoji (Slack; contracts emoji.ts): frecency ranking of my uses (how often, weighted by how
 * recently), shared by the server (which stores the counts) and clients (optimistic bumps). Shown first in the emoji
 * pickers, and the top three are the message hover bar's quick reactions.
 */
export type EmojiUse = { emoji: string; count: number; lastUsed: number };

const DAY = 86_400_000;

/** Slack's starting set for someone with no history (and the padding after their own). */
export const DEFAULT_FREQUENT = ["👍", "✅", "👀", "🙌", "😂", "🎉", "❤️", "🙏", "🔥", "💯"];

/** Most emoji the list keeps. */
export const FREQUENT_LIMIT = 24;

/** count × a recency weight: ×1 within a day, ×0.7 a week, ×0.5 a month, ×0.3 three months, ×0.1 older. */
export function frecency(use: EmojiUse, now: number): number {
  const age = Math.max(0, now - use.lastUsed);
  const w = age <= DAY ? 1 : age <= 7 * DAY ? 0.7 : age <= 30 * DAY ? 0.5 : age <= 90 * DAY ? 0.3 : 0.1;
  return use.count * w;
}

/** My emoji, best first (score, then most recent, then by emoji), at most `limit`. */
export function rankFrequent(uses: readonly EmojiUse[], now: number, limit = FREQUENT_LIMIT): string[] {
  return [...uses]
    .sort((a, b) => frecency(b, now) - frecency(a, now) || b.lastUsed - a.lastUsed || (a.emoji < b.emoji ? -1 : a.emoji > b.emoji ? 1 : 0))
    .slice(0, limit)
    .map((u) => u.emoji);
}

/** My ranked emoji padded with the defaults (no duplicates) up to `n`. */
export function withDefaults(ranked: readonly string[], n: number, defaults: readonly string[] = DEFAULT_FREQUENT): string[] {
  const out = ranked.slice(0, n);
  for (const d of defaults) {
    if (out.length >= n) break;
    if (!out.includes(d)) out.push(d);
  }
  return out;
}

/** A use just now, as the client shows it before the server's list comes back: moves to the front. */
export function bumpFrequent(list: readonly string[], emoji: string, limit = FREQUENT_LIMIT): string[] {
  return [emoji, ...list.filter((e) => e !== emoji)].slice(0, limit);
}

/** The hover bar's quick reactions: my top three (padded with the defaults). */
export function quickReactions(frequent: readonly string[]): string[] {
  return withDefaults(frequent, 3);
}
